import { Table } from 'antd';
import React, { useMemo } from 'react';
import moment from 'moment';
import { COLORS, DAYTIME_FORMAT, ORDER_STATUSES_DISPLAY, STEP_TYPE } from '../../constants';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useColumnConfig from '../../hooks/useColumnConfig';
import ColumnConfigDrawer from '../ColumnConfigDrawer';

// Anchors for fixed (un-hidable, un-movable) columns. Match the column-config
// drawer's contract used by OrdersTable. `action` is fixed when withAction is
// supplied so the per-row dot menu always anchors to the right.
const FIXED_KEYS = ['orderCode', 'status', 'action'];

export default function RouteOrdersTable({
  dataSource,
  selectedRowKeys,
  onSelectChange,
  showIndexVisit,
  routes,
  solutions,
  configRef,
  withAction,
  ...props
}) {
  const { t } = useTranslation('routes');
  const navigate = useNavigate();

  // OVO-026 — column definitions wrapped in useMemo so useColumnConfig's
  // reference equality holds. Mirrors the OrdersTable pattern.
  const allColumns = useMemo(() => {
    const columns = [
      {
        title: t('routeOrdersTable.orderId'),
        dataIndex: 'orderCode',
        key: 'orderCode',
        fixed: 'left',
        width: 200,
        className: 'border-right',
        render: (value, record) => {
          if (showIndexVisit) {
            const route = routes.find((route) => route.id === record.routeId);
            const driverId = route?.driver?.id || 'unscheduled';
            const pickupIndex = solutions[driverId]?.findIndex(
              (s) => s.type === STEP_TYPE.pickup && (s.orderId === record.id || s.orderIds?.includes(record.id)),
            );
            const deliveryIndex = solutions[driverId]?.findIndex(
              (s) => s.type === STEP_TYPE.delivery && s.orderIds?.includes(record.id),
            );
            const color = route?.driver?.id ? COLORS[route.driver.id % COLORS.length] : '606060';

            return (
              <div
                style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => {
                  navigate(`/admin/orders?q=${record.orderCode}`);
                }}
              >
                <span
                  style={{
                    border: `1px solid #${color}`,
                    color: `#${color}`,
                    textAlign: 'center',
                    minWidth: '22px',
                    minHeight: '22px',
                    lineHeight: '20px',
                    borderRadius: '50%',
                    fontSize: '11px',
                    letterSpacing: -1,
                    paddingRight: 1,
                  }}
                >
                  {pickupIndex + 1}
                </span>
                <span
                  style={{
                    border: `1px solid #${color}`,
                    color: `#${color}`,
                    textAlign: 'center',
                    minWidth: '22px',
                    minHeight: '22px',
                    lineHeight: '20px',
                    borderRadius: 4,
                    fontSize: '11px',
                    marginLeft: '6px',
                    marginRight: '3px',
                    letterSpacing: -1,
                    paddingRight: 1,
                  }}
                >
                  {deliveryIndex + 1}
                </span>
                {value}
              </div>
            );
          } else {
            return value;
          }
        },
      },
      {
        title: t('routeOrdersTable.name'),
        dataIndex: 'name',
        key: 'name',
        width: 200,
      },
      {
        title: t('routeOrdersTable.createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: 250,
        render: (value) => {
          return value ? moment(value).format(DAYTIME_FORMAT) : '';
        },
      },
      {
        title: t('routeOrdersTable.pickupAddress'),
        dataIndex: 'pickupAddress',
        key: 'pickupAddress',
        width: 700,
      },
      {
        title: t('routeOrdersTable.deliveryAddress'),
        dataIndex: 'deliveryAddress',
        key: 'deliveryAddress',
        width: 700,
      },
      {
        title: t('routeOrdersTable.deliveryPhone'),
        dataIndex: 'deliveryPhone',
        key: 'deliveryPhone',
        width: 200,
      },
      {
        title: t('routeOrdersTable.pickupTimeWindow'),
        dataIndex: 'pickupTimeWindow',
        key: 'pickupTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.pickupTimeStart || '') + ' - ' + (record.pickupTimeEnd || '');
        },
      },
      {
        title: t('routeOrdersTable.deliveryTimeWindow'),
        dataIndex: 'deliveryTimeWindow',
        key: 'deliveryTimeWindow',
        width: 200,
        ellipsis: true,
        render(_value, record) {
          return (record.deliveryTimeStart || '') + ' - ' + (record.deliveryTimeEnd || '');
        },
      },
      {
        title: t('routeOrdersTable.load'),
        dataIndex: 'load',
        key: 'load',
        width: 200,
      },
      {
        title: t('routeOrdersTable.status'),
        dataIndex: 'status',
        key: 'status',
        fixed: 'right',
        width: 200,
        className: 'border-left',
        render: (value) => {
          return <span className={'status order ' + value.toLowerCase()}>{ORDER_STATUSES_DISPLAY[value]}</span>;
        },
      },
    ];

    if (showIndexVisit) {
      // Driver Name + Route ID are only relevant in the 'orders' subview where
      // routes already exist. Inserted right after the orderCode column.
      columns.splice(1, 0, {
        title: t('routeOrdersTable.driverName'),
        key: 'driverName',
        width: 200,
        render: (record) => {
          const route = routes.find((route) => route.id === record.routeId);
          return route?.driver?.name || '';
        },
      });
      columns.splice(1, 0, {
        title: t('routeOrdersTable.routeId'),
        key: 'routeId',
        width: 200,
        render: (record) => {
          const route = routes.find((route) => route.id === record.routeId);
          return route?.id || '';
        },
      });
    }

    // OVO-026 — per-row dot menu, mirrors OrdersTable's `action` column.
    // Fixed-right so it stays visible while horizontally scrolling.
    if (withAction) {
      columns.push({
        key: 'action',
        width: 120,
        fixed: 'right',
        render(record) {
          return withAction(record);
        },
      });
    }

    return columns;
    // showIndexVisit affects the render and the column shape; routes / solutions
    // referenced inside renders — keep them in the deps so the table re-renders
    // when the underlying day plan changes.
  }, [t, showIndexVisit, routes, solutions, navigate, withAction]);

  // OVO-026 — distinct localStorage key so the route-plan Orders view keeps
  // its own column preferences. Sharing the key with /admin/orders would mean
  // every column tweak on one page propagates to the other, which gets
  // confusing because the two pages have different column sets.
  const columnConfig = useColumnConfig('route-orders-columns', allColumns, FIXED_KEYS);

  // Expose openDrawer + pageSize to the parent (RoutesChart toolbar) via ref.
  if (configRef) {
    configRef.current = { openDrawer: columnConfig.openDrawer, pageSize: columnConfig.pageSize };
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="table-orders table-common">
      <Table
        columns={columnConfig.visibleColumns}
        rowSelection={rowSelection}
        dataSource={dataSource}
        scroll={{ x: 'max-content', y: 'max-content' }}
        // Use the configured page size; ColumnConfigDrawer is the single source
        // of truth (no duplicate dropdown at the bottom — same OVO-022 pattern
        // as the PartnerDetail Routes tab).
        pagination={{ pageSize: columnConfig.pageSize, showSizeChanger: false }}
        // bordered
        {...props}
      />
      <ColumnConfigDrawer
        open={columnConfig.drawerOpen}
        onClose={columnConfig.closeDrawer}
        columns={columnConfig.orderedAllColumns}
        hidden={columnConfig.hidden}
        fixedKeys={FIXED_KEYS}
        onToggle={columnConfig.toggleColumn}
        onMove={columnConfig.moveColumn}
        onReset={columnConfig.resetConfig}
        pageSize={columnConfig.pageSize}
        onPageSizeChange={columnConfig.setPageSize}
      />
    </div>
  );
}
